import { Component } from '@theme/component';

/**
 * @typedef {Object} CollapsibleGalleryRefs
 * @property {HTMLElement} gallery - The desktop media grid.
 * @property {HTMLElement[]} mediaItems - The media tiles in the grid.
 * @property {HTMLButtonElement} revealButton - The button that toggles the grid.
 */

/**
 * Keeps the first two media rows and part of the third row visible until the customer expands the gallery.
 *
 * @extends {Component<CollapsibleGalleryRefs>}
 */
class FabiniCollapsibleGallery extends Component {
  requiredRefs = ['gallery', 'mediaItems', 'revealButton'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const firstItem = this.refs.mediaItems?.[0];
    const firstRevealedItem = this.refs.mediaItems?.[4];

    if (!firstItem || !firstRevealedItem) return;

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#updateCollapsedHeight);
      this.#resizeObserver.observe(firstItem);
      this.#resizeObserver.observe(firstRevealedItem);
    }

    this.#updateCollapsedHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  #updateCollapsedHeight = () => {
    const firstRevealedItem = this.refs.mediaItems?.[4];

    if (this.dataset.expanded === 'true' || !firstRevealedItem || firstRevealedItem.offsetHeight === 0) return;

    const collapsedHeight = firstRevealedItem.offsetTop + firstRevealedItem.offsetHeight * 0.75;

    this.style.setProperty('--fabini-media-gallery-collapsed-height', `${Math.round(collapsedHeight)}px`);
    this.#setRevealedItemsInert(true);
    this.dataset.ready = 'true';
  };

  expand = () => {
    this.#setRevealedItemsInert(false);
    this.dataset.expanded = 'true';
    this.refs.revealButton.setAttribute('aria-expanded', 'true');

    const firstRevealedControl = this.refs.mediaItems?.[4]?.querySelector('button, video, model-viewer, a');

    if (firstRevealedControl instanceof HTMLElement) {
      requestAnimationFrame(() => firstRevealedControl.focus({ preventScroll: true }));
    }
  };

  collapse = () => {
    const revealButtonTop = this.refs.revealButton.getBoundingClientRect().top;

    this.dataset.expanded = 'false';
    this.refs.revealButton.setAttribute('aria-expanded', 'false');
    this.#setRevealedItemsInert(true);

    const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    requestAnimationFrame(() => {
      const revealButtonOffset = this.refs.revealButton.getBoundingClientRect().top - revealButtonTop;

      window.scrollBy({ top: revealButtonOffset, behavior: scrollBehavior });
    });
  };

  toggle = () => {
    if (this.dataset.expanded === 'true') {
      this.collapse();
      return;
    }

    this.expand();
  };

  /**
   * Prevents keyboard and assistive-technology access to media hidden below the crop.
   *
   * @param {boolean} inert
   */
  #setRevealedItemsInert(inert) {
    this.refs.mediaItems.slice(4).forEach((item) => {
      item.inert = inert;
    });
  }
}

if (!customElements.get('fabini-collapsible-gallery')) {
  customElements.define('fabini-collapsible-gallery', FabiniCollapsibleGallery);
}
